import { useContext, useEffect, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2, FiUser } from "react-icons/fi";
import LogicContext from "../../Context/LogicContext";
import { employeePersonalInfoService } from "../../services/employeePersonalInfo.service";
import { DocumentDeleteModal, ModalShell } from "./EmployeeDocumentsTab";

const EMPTY_FORM = {
  fathersName: "",
  mothersName: "",
  religionId: "",
  bloodGroupId: "",
  maritalStatusId: "",
  personalEmail: "",
  personalMobile: "",
  emergencyContact: "",
  relationshipId: "",
};

const EMPTY_OPTIONS = { religions: [], bloodGroups: [], maritalStatuses: [], relationships: [] };

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-3 py-2 text-xs @xl:grid-cols-[minmax(0,140px)_minmax(0,1fr)]">
      <dt className="text-gray-600">{label}</dt>
      <dd className="break-words font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function TextField({ label, value, required, maxLength, inputMode, onChange }) {
  return (
    <>
      <label className="mt-4 block text-xs font-medium text-gray-800">
        {label}
        {required ? "*" : ""}
      </label>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder="Input Text"
        className="mt-1 h-10 w-full rounded-md bg-slate-100 px-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </>
  );
}

function SelectField({ label, value, options, required, onChange }) {
  return (
    <>
      <label className="mt-4 block text-xs font-medium text-gray-800">
        {label}
        {required ? "*" : ""}
      </label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-10 w-full rounded-md border-0 bg-slate-100 px-3 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select...</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </>
  );
}

function toChoices(items, idField, nameField) {
  return items.map((item) => ({ value: String(item[idField]), label: item[nameField] }));
}

function PersonalInfoFormModal({ mode, initial, options, onSubmit, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function setField(field) {
    return (value) => {
      setForm((current) => ({ ...current, [field]: value }));
      setError("");
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const values = {
      ...form,
      fathersName: form.fathersName.trim(),
      mothersName: form.mothersName.trim(),
      personalEmail: form.personalEmail.trim(),
      personalMobile: form.personalMobile.trim(),
      emergencyContact: form.emergencyContact.trim(),
    };
    if (!values.fathersName) return setError("Father's name is required.");
    if (!values.mothersName) return setError("Mother's name is required.");
    if (!values.religionId) return setError("Religion is required.");
    if (!values.maritalStatusId) return setError("Marital status is required.");
    if (!values.personalMobile) return setError("Personal mobile is required.");
    if (!/^\+?[\d\s-]{7,20}$/.test(values.personalMobile)) return setError("Enter a valid personal mobile number.");
    if (values.personalEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.personalEmail)) {
      return setError("Enter a valid personal email address.");
    }
    if (values.emergencyContact && !/^\+?[\d\s-]{7,20}$/.test(values.emergencyContact)) {
      return setError("Enter a valid emergency contact number.");
    }

    setSaving(true);
    try {
      await onSubmit(values);
    } catch (submitError) {
      setError(submitError?.message || "Unable to save personal information.");
      setSaving(false);
    }
  }

  return (
    <ModalShell open onClose={saving ? () => {} : onClose}>
      <form onSubmit={handleSubmit}>
        <div className="max-h-[70vh] overflow-y-auto px-6 pt-6">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === "edit" ? "Edit Personal Information" : "Add Personal Information"}
          </h2>
          <TextField label="Father's Name" required maxLength={100} value={form.fathersName} onChange={setField("fathersName")} />
          <TextField label="Mother's Name" required maxLength={100} value={form.mothersName} onChange={setField("mothersName")} />
          <SelectField
            label="Religion"
            required
            value={form.religionId}
            options={toChoices(options.religions, "religion_id", "religion_name")}
            onChange={setField("religionId")}
          />
          <SelectField
            label="Blood Group"
            value={form.bloodGroupId}
            options={toChoices(options.bloodGroups, "blood_group_id", "blood_group_name")}
            onChange={setField("bloodGroupId")}
          />
          <SelectField
            label="Marital Status"
            required
            value={form.maritalStatusId}
            options={toChoices(options.maritalStatuses, "marital_status_id", "marital_status_name")}
            onChange={setField("maritalStatusId")}
          />
          <TextField label="Personal Email" maxLength={100} value={form.personalEmail} onChange={setField("personalEmail")} />
          <TextField
            label="Personal Mobile"
            required
            maxLength={20}
            inputMode="tel"
            value={form.personalMobile}
            onChange={setField("personalMobile")}
          />
          <TextField
            label="Emergency Contact"
            maxLength={20}
            inputMode="tel"
            value={form.emergencyContact}
            onChange={setField("emergencyContact")}
          />
          <SelectField
            label="Relationship"
            value={form.relationshipId}
            options={toChoices(options.relationships, "relationship_id", "relationship_name")}
            onChange={setField("relationshipId")}
          />
          {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-md bg-slate-100 px-5 text-xs font-medium text-gray-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EmployeePersonalInfoTab({ employeeId }) {
  const logicContext = useContext(LogicContext);
  const [record, setRecord] = useState(null);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function loadPersonalInfo() {
    setLoading(true);
    setError("");
    try {
      const [info, choices] = await Promise.all([
        employeePersonalInfoService.getPersonalInfo(employeeId),
        employeePersonalInfoService.getPersonalInfoOptions(),
      ]);
      setRecord(info);
      setOptions(choices);
    } catch (loadError) {
      setRecord(null);
      setError(loadError?.message || "Unable to load personal information.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPersonalInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  async function handleSave(values) {
    const result =
      formMode === "edit"
        ? await employeePersonalInfoService.updatePersonalInfo(employeeId, values)
        : await employeePersonalInfoService.createPersonalInfo(employeeId, values);
    logicContext.showToast(result.message, "success");
    setFormMode("");
    await loadPersonalInfo();
  }

  async function handleDelete() {
    setConfirmDelete(false);
    try {
      const result = await employeePersonalInfoService.deletePersonalInfo(employeeId);
      logicContext.showToast(result.message, "success");
      await loadPersonalInfo();
    } catch (deleteError) {
      logicContext.showToast(deleteError?.message || "Unable to delete personal information.", "error");
    }
  }

  const editInitial = record
    ? {
        fathersName: record.fathersName === "-" ? "" : record.fathersName,
        mothersName: record.mothersName === "-" ? "" : record.mothersName,
        religionId: String(record.religionId ?? ""),
        bloodGroupId: String(record.bloodGroupId ?? ""),
        maritalStatusId: String(record.maritalStatusId ?? ""),
        personalEmail: record.personalEmail === "-" ? "" : record.personalEmail,
        personalMobile: record.personalMobile === "-" ? "" : record.personalMobile,
        emergencyContact: record.emergencyContact === "-" ? "" : record.emergencyContact,
        relationshipId: String(record.relationshipId ?? ""),
      }
    : null;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white">
            <FiUser className="h-4 w-4" />
          </span>
          <h2 className="text-sm font-semibold text-gray-900">Employee Personal Information</h2>
        </div>
        {!loading && !error ? (
          record ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormMode("edit")}
                aria-label="Edit personal information"
                className="text-gray-700 hover:text-blue-500"
              >
                <FiEdit2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete personal information"
                className="text-red-500 hover:text-red-700"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setFormMode("create")}
              className="flex h-8 items-center gap-1.5 rounded-md border border-blue-500 px-3 text-xs font-medium text-blue-500 hover:bg-blue-50"
            >
              <FiPlus className="h-3.5 w-3.5" />
              Add Personal Information
            </button>
          )
        ) : null}
      </div>

      {loading ? <p className="mt-5 text-xs text-gray-500">Loading personal information...</p> : null}
      {!loading && error ? (
        <div className="mt-5 rounded-lg border border-red-100 bg-red-50 p-4 text-xs text-red-700">{error}</div>
      ) : null}
      {!loading && !error && !record ? (
        <p className="mt-5 text-xs text-gray-500">No personal information has been added for this employee yet.</p>
      ) : null}
      {!loading && !error && record ? (
        <div className="mt-5 grid grid-cols-1 gap-x-10 @3xl:grid-cols-2">
          <dl>
            <InfoRow label="Father's Name" value={record.fathersName} />
            <InfoRow label="Mother's Name" value={record.mothersName} />
            <InfoRow label="Religion" value={record.religionName} />
            <InfoRow label="Blood Group" value={record.bloodGroupName} />
            <InfoRow label="Marital Status" value={record.maritalStatusName} />
          </dl>
          <dl>
            <InfoRow label="Personal Email" value={record.personalEmail} />
            <InfoRow label="Personal Mobile" value={record.personalMobile} />
            <InfoRow label="Emergency Contact" value={record.emergencyContact} />
            <InfoRow label="Relationship" value={record.relationshipName} />
          </dl>
        </div>
      ) : null}

      {formMode ? (
        <PersonalInfoFormModal
          mode={formMode}
          initial={formMode === "edit" ? editInitial : null}
          options={options}
          onSubmit={handleSave}
          onClose={() => setFormMode("")}
        />
      ) : null}
      {confirmDelete ? (
        <DocumentDeleteModal onConfirm={handleDelete} onClose={() => setConfirmDelete(false)} />
      ) : null}
    </>
  );
}

export default EmployeePersonalInfoTab;
